import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import { Divider } from '@mui/material';
import moment from 'moment';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { user } from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import _ from 'lodash';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import useOpenState from '../../../../hooks/useOpenState';
import { AgyleTable, createAgyleTableColumn } from '../../../../components/table/AgyleTable';
import uuidv4 from 'uuid/v4';
import MessageBox from '../../../../util/messageBox';
import { setSnackbar } from '../../../../store/reducers/globals';
import Notes from '../../../../util/notes';
import { calculateArrivalDate } from '../../../../util/patientUtil';
import MiniFormDialog from '../../../../components/modal/miniFormDialog';
import ControlClinician from '../../../../controls/agyle/clinician/controlClinician';

const DEFAULT_VALUE = {
    who: null,
    when: moment(),
    drugs: [],
    printCount: 0,
    lastPrintDate: null,
    lastPrintBy: null,
};

const DEFAULT_VALUE_DRUG = {
    drug: '',
    dose: '',
    route: '',
    frequency: '',
    duration: '',
};

const COLUMNS = [
    createAgyleTableColumn('drug', 'Drug', 1, 'drug', 'string', 'notSort', 'medium', 'center', true, false),
    createAgyleTableColumn('dose', 'Dose', 2, 'dose', 'string', 'notSort', 'medium', 'center', true, false),
    createAgyleTableColumn('route', 'Route', 3, 'route', 'string', 'notSort', 'medium', 'center', true, false),
    createAgyleTableColumn('frequency', 'Frequency', 4, 'frequency', 'string', 'notSort', 'medium', 'center', true, false),
    createAgyleTableColumn('duration', 'Duration', 5, 'duration', 'string', 'notSort', 'medium', 'center', true, false),
    createAgyleTableColumn('action', 'Action', 6, null, 'action', 'notSort', 'medium', 'center', true, true, null, null),
];

const PrescriptionForm = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // User Section
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    // Form Section
    const [formIsModified, setFormIsModified] = useState(false);
    const [prescription, setPrescription] = useState(() => ({
        ...DEFAULT_VALUE,
        when: moment(),
    }));

    const [drug, setDrug] = useState(DEFAULT_VALUE_DRUG);
    const [disable, setDisable] = useState(false);

    // Error States
    const [dateError, setDateError] = useState(null);
    const [error, setError] = useState(false);
    const [errorDrug, setErrorDrug] = useState(false);

    // Other States
    const [expanded, setExpanded] = useState(true);
    const [isNewDrugOpen, openNewDrug, closeNewDrug] = useOpenState(false, true);
    const [isMaxDrugOpen, openMaxDrug, closeMaxDrug] = useOpenState(false, true);

    // Confirm delete
    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);
    const tempObj = useRef(null);

    useEffect(() => {
        if (props.agyleDialogInsertButtons) {
            props.agyleDialogInsertButtons([
                <AgyleButton variant="contained" onClick={handleSave} disabled={disable || !formIsModified}>
                    {t('Submit')}
                </AgyleButton>,
            ]);
        }

        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSave);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(props.disableSave || !_.isNil(props.selectedHistoryForm) || !formIsModified);
        }
        setDisable(props.disableSave || !_.isNil(props.selectedHistoryForm));
    }, [props.disableSave, prescription, formIsModified, props.selectedHistoryForm]);

    useEffect(() => {
        if (!_.isNil(props.selectedHistoryForm)) {
            setPrescription(props.selectedHistoryForm.values);
        }
    }, [props.selectedHistoryForm]);

    const handleSave = () => {
        if (valid()) {
            let clone = _.cloneDeep(prescription);
            clone.printCount += 1;
            clone.lastPrintDate = moment().toISOString();
            clone.lastPrintBy = userObject?.userName;

            const id = uuidv4() + '';
            const data = [
                {
                    name: 'studies.prescription',
                    value: {
                        id: id,
                        date: moment().toISOString(),
                        who: userObject?.userName,
                        by: tester,
                        values: clone,
                        parentForm: props.parentForm,
                    },
                    noteToAddWithRefId: Notes.getPrescriptionNote(clone),
                    idToTrack: id,
                    referenceId: id,
                },
            ];

            if (props.onSave) props.onSave(data);
            if (props.onChange) props.onChange(data);

            if (props.onClose) props.onClose();
        } else {
            dispatch(
                setSnackbar({
                    type: 'warning',
                    message: t('Clinician, Prescription Date and Time and at least one drug is mandatory!'),
                }),
            );
            return false;
        }
    };

    const handleChangeTextFieldDrug = (event) => {
        const { value, name } = event.target;
        setDrug((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleCloseNewDrag = () => {
        closeNewDrug();
        setDrug(DEFAULT_VALUE_DRUG);
        setErrorDrug(false);
    };

    const handleCloseConfirm = () => {
        closeConfirm();
        tempObj.current = null;
    };

    const handleAddNewDrug = () => {
        if (validDrug()) {
            const obj = {
                ...drug,
                id: uuidv4() + '',
                by: userObject?.userName,
                who: tester,
                when: moment().toISOString(),
            };

            const clone = _.cloneDeep(prescription.drugs);
            clone.push(obj);
            setPrescription((prevState) => ({ ...prevState, drugs: clone }));
            handleCloseNewDrag();
        }
    };

    const handleOpenAddDrug = () => {
        if (prescription.drugs.length < 10) openNewDrug();
        else openMaxDrug();
    };

    const valid = () => {
        const isValid = !_.isEmpty(prescription.drugs) && !_.isEmpty(prescription.when) && !_.isEmpty(prescription.when) && !dateError;
        setError(!isValid);
        return isValid;
    };

    const validDrug = () => {
        const isValid = Object.values(drug).every((value) => value !== '');
        setErrorDrug(!isValid);
        return isValid;
    };

    const handleDeleteDrug = (e) => {
        tempObj.current = e;
        openConfirm();
    };

    const handleDeleteDrugFromArray = () => {
        if (tempObj.current) {
            closeConfirm();

            let clone = _.cloneDeep(prescription.drugs);
            clone = _.filter(clone, (d) => d.id !== tempObj.current?.id);
            setPrescription((prevState) => ({ ...prevState, drugs: clone }));

            tempObj.current = null;
        }
    };

    return (
        <>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <AgyleDatePicker
                        label={t('Prescription Date and Time')}
                        value={prescription.when}
                        size="large"
                        onChange={(event) => {
                            setPrescription((prevState) => ({ ...prevState, when: event }));
                            setFormIsModified(true);
                        }}
                        type="dateTime"
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDate={moment()}
                        error={Boolean(dateError || (error && _.isEmpty(prescription.when)))}
                        onError={setDateError}
                        disabled={disable}
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={6}>
                    <ControlClinician
                        label={t('Clinician')}
                        agyleRefresh
                        size="large"
                        value={prescription?.who}
                        onChange={(event) => {
                            setPrescription((prevState) => ({ ...prevState, who: event }));
                            setFormIsModified(true);
                        }}
                        findUser={userObject?.userName}
                        error={error && _.isEmpty(prescription.who)}
                        disabled={disable}
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={6}>
                    <AgyleTextField
                        label={t('GMC/NMC')}
                        readOnly
                        value={prescription.who?.__code || ''}
                        fullWidth
                        disabled={disable}
                        size="large"
                        noBorder={false}
                        withNewBackground
                    />
                </Grid>

                <Grid item xs={12}>
                    <Divider />
                </Grid>

                {!_.isEmpty(prescription.drugs) && (
                    <Grid item xs={12}>
                        <AgyleAccordion
                            expanded={expanded}
                            onChange={() => setExpanded((prevState) => !prevState)}
                            label={t('Drugs')}
                            formBackground
                        >
                            <AgyleTable
                                columns={COLUMNS}
                                data={prescription.drugs}
                                disableFilter
                                deleteItem={handleDeleteDrug}
                                disabledAction={disable}
                            />
                        </AgyleAccordion>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <AgyleButton onClick={handleOpenAddDrug} variant="contained" disabled={disable}>
                        {t('Add Drug')}
                    </AgyleButton>
                </Grid>
            </Grid>

            <MiniFormDialog open={isNewDrugOpen} onClose={handleCloseNewDrag} title={t('Add New Drug')} fullWidth onSave={handleAddNewDrug}>
                <>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <AgyleTextField
                                name="drug"
                                label={t('Drug')}
                                value={drug.drug}
                                onChange={handleChangeTextFieldDrug}
                                fullWidth
                                size="large"
                                debounce
                                error={errorDrug && _.isEmpty(drug.drug)}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleTextField
                                name="dose"
                                label={t('Dose')}
                                value={drug.dose}
                                onChange={handleChangeTextFieldDrug}
                                fullWidth
                                size="large"
                                debounce
                                error={errorDrug && _.isEmpty(drug.dose)}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleTextField
                                name="route"
                                label={t('Route')}
                                value={drug.route}
                                onChange={handleChangeTextFieldDrug}
                                fullWidth
                                size="large"
                                debounce
                                error={errorDrug && _.isEmpty(drug.route)}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleTextField
                                name="frequency"
                                label={t('Frequency')}
                                value={drug.frequency}
                                onChange={handleChangeTextFieldDrug}
                                fullWidth
                                size="large"
                                debounce
                                error={errorDrug && _.isEmpty(drug.frequency)}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleTextField
                                name="duration"
                                label={t('Duration')}
                                value={drug.duration}
                                onChange={handleChangeTextFieldDrug}
                                fullWidth
                                size="large"
                                debounce
                                error={errorDrug && _.isEmpty(drug.duration)}
                                noBorder={false}
                                withNewBackground
                            />
                        </Grid>
                    </Grid>
                </>
            </MiniFormDialog>

            <MessageBox
                title={t('Confirm')}
                open={isConfirmOpen}
                haveAccept={{ yes: t('Yes'), no: t('No') }}
                warning
                onAccept={handleDeleteDrugFromArray}
                onClose={handleCloseConfirm}
                message={t('Are you sure you want to delete selected drug record?')}
            />

            <MessageBox
                title={t('Information')}
                open={isMaxDrugOpen}
                haveAccept={{ no: t('Close') }}
                warning
                onClose={closeMaxDrug}
                message={t('Maximum of 10 drugs added per prescription.')}
            />
        </>
    );
};

export default PrescriptionForm;
